import type {
  FilaBebidaDescricao,
  FilaBebidaItem,
} from "../../../services/filaBebidasService";

export type FilaBebidaItemAgrupado = FilaBebidaItem & {
  quantidade: number;
  itemIds: number[];
};

function resolveDescricao(item: FilaBebidaItem): FilaBebidaDescricao | null {
  if (Array.isArray(item.descricao) && item.descricao.length > 0) {
    return item.descricao[0];
  }
  return null;
}

function normalizeAdicionaisKey(item: FilaBebidaItem): string {
  const desc = resolveDescricao(item);
  if (desc?.adicionais?.length) {
    return desc.adicionais.join("|");
  }
  if (!Array.isArray(item.adicionais)) return "";
  return item.adicionais
    .map((a) => {
      const nome = String(a.nome || "").trim().toLowerCase();
      if (!nome) return null;
      const q =
        a.quantidade != null && a.quantidade > 1
          ? `x${Math.floor(a.quantidade)}`
          : "";
      return `${nome}${q}`;
    })
    .filter((v): v is string => v != null)
    .sort()
    .join("|");
}

function buildGroupKey(item: FilaBebidaItem): string {
  const desc = resolveDescricao(item);
  const nome = String(desc?.name || item.produtoNome || "")
    .trim()
    .toLowerCase();
  const obs = String(desc?.observacao || item.observacao || "")
    .trim()
    .toLowerCase();
  const produtoId = String(item.produtoIdFoodfly || "").trim();

  return [
    item.numeroPedido,
    item.pedidoFoodflyId,
    item.status,
    nome,
    normalizeAdicionaisKey(item),
    obs,
    produtoId,
  ].join("::");
}

/** Agrupa bebidas idênticas do mesmo pedido (ex.: 3x Coca-Cola em um card). */
export function agruparItensFilaBebidas(
  items: FilaBebidaItem[],
): FilaBebidaItemAgrupado[] {
  const map = new Map<string, FilaBebidaItem[]>();

  for (const item of items) {
    const key = buildGroupKey(item);
    const grupo = map.get(key);
    if (grupo) grupo.push(item);
    else map.set(key, [item]);
  }

  const grupos: FilaBebidaItemAgrupado[] = [];

  for (const grupo of Array.from(map.values())) {
    grupo.sort((a, b) => a.id - b.id);
    const principal = grupo[0];
    grupos.push({
      ...principal,
      quantidade: grupo.length,
      itemIds: grupo.map((i) => i.id),
    });
  }

  grupos.sort(
    (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return grupos;
}

export function formatBebidaNomeComQuantidade(
  nome: string,
  quantidade: number,
): string {
  const label = nome.trim();
  if (!label) return quantidade > 1 ? `${quantidade}x` : "";
  return quantidade > 1 ? `${quantidade}x ${label}` : label;
}
